// Copyright 2022, Imprivata, Inc.  All rights reserved.

/**
 * Utility to preload SVG files for faster rendering
 */
const cache = new Map<string, Promise<string>>();

/**
 * Preload an SVG file and cache it
 */
export const preloadSvg = (url: string): Promise<string> => {
  if (cache.has(url)) {
    return cache.get(url) as Promise<string>;
  }

  const promise: Promise<string> = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.statusText}`);
      }
      return response.text();
    })
    .catch((error) => {
      // Remove failed promise from cache so it can be retried
      cache.delete(url);
      throw error;
    });

  cache.set(url, promise);
  return promise;
};

/**
 * Get cached SVG content if available
 */
export const getCachedSvg = (url: string): Promise<string> | null => {
  return cache.get(url) || null;
};

/**
 * Clear all cached SVGs
 */
export const clearSvgCache = (): void => {
  cache.clear();
};

/**
 * Preload multiple SVGs concurrently
 */
export const preloadMultipleSvgs = (urls: string[]): Promise<string[]> => {
  return Promise.all(urls.map((url) => preloadSvg(url)));
};
